import { currencyFormatter } from './formatters'

export const CAPITAL_EVENT_TYPES = [
	'promo_tile_viewed',
	'application_started',
	'application_step_changed',
	'application_submitted',
	'financing_loaded',
	'capital_gap_guide_opened',
	'how_will_you_use_capital_guide_opened'
] as const

export type CapitalEventType = (typeof CAPITAL_EVENT_TYPES)[number]

export interface CapitalOfferFields {
	offerId: string | null
	offerAmount: number | null
	offerStatus: string | null
}

export interface CapitalOfferSummary extends CapitalOfferFields {
	// Backend's classification of `offerStatus` — do not re-derive it here.
	promo: boolean
}

// Entry paths into `/financing/capital`: `email`/`pdf` via `?source=` query
// param, `tile`/`banner` via router navigation state, `nav` as the fallback.
export const CAPITAL_ENTRY_SOURCES = ['tile', 'email', 'nav', 'banner', 'pdf'] as const

export type CapitalEntrySource = (typeof CAPITAL_ENTRY_SOURCES)[number]

export const resolveCapitalEntrySource = (
	queryValue: string | null,
	stateValue: unknown
): CapitalEntrySource => {
	const candidate = queryValue ?? stateValue
	return (CAPITAL_ENTRY_SOURCES as readonly unknown[]).includes(candidate)
		? (candidate as CapitalEntrySource)
		: 'nav'
}

/**
 * Formats a Stripe-offered amount (smallest currency unit — cents) as a
 * whole-dollar USD string like "$10,000". Capital offers are always round
 * amounts so trailing cents are stripped.
 */
export const formatCapitalOfferAmount = (amountInCents: number): string =>
	currencyFormatter.format(Math.round(amountInCents / 100)).replace(/\.00$/, '')

/**
 * Same number as `formatCapitalOfferAmount` but without the leading `$` — for
 * pages that render the currency symbol at a different size next to the number.
 */
export const formatCapitalOfferDollars = (amountInCents: number): string =>
	Math.round(amountInCents / 100).toLocaleString('en-US')

// Campaign params the backend's validation schema allows. Renaming one silently
// breaks the campaign query, so this list is the contract.
export const CAPITAL_ATTRIBUTION_PARAMS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'source'
] as const

export type CapitalAttribution = Record<string, string>

// One key per origin, not per practice: the click happens before any practice is
// selected, so the touch is person-level and follows a practice switch. Logout
// clears it (see `isScratchLocalStorageKey`) so a shared terminal never credits
// one user's click to the next.
const ATTRIBUTION_KEY = 'capital_attribution'

// First touch wins for 30 days: landing on the root and reaching /capital can be
// separated by days, so the window has to outlive the browser session.
export const CAPITAL_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000

// Matches the backend's per-value cap, which truncates rather than rejects.
const MAX_VALUE_LENGTH = 255

/** Stored first-touch attribution, or null when absent, expired or unreadable. */
export const readCapitalAttribution = (): CapitalAttribution | null => {
	try {
		const raw = window.localStorage.getItem(ATTRIBUTION_KEY)
		if (!raw) return null
		const { storedAt, ...attribution } = JSON.parse(raw)
		if (typeof storedAt !== 'number') return null
		if (Date.now() - storedAt > CAPITAL_ATTRIBUTION_TTL_MS) return null
		return attribution
	} catch {
		// absent, corrupt, or storage blocked — all mean "no attribution"
		return null
	}
}

/**
 * Captures campaign params from the current URL, on whatever route the user
 * landed on. Call once at app bootstrap, before the login flow strips the query
 * string. Reads `window.location` and `document.referrer` itself so callers
 * cannot pass a route that has already been rewritten. Does not modify the URL.
 */
export const captureCapitalAttribution = (): void => {
	const params = new URLSearchParams(window.location.search)

	// A `utm_*` key is what makes this a campaign. `?source=email` and `?source=pdf`
	// alone are the entry-source deep links in CAPITAL_ENTRY_SOURCES, and treating
	// one as a touch would hold the 30-day slot against the real click.
	const isCampaign = CAPITAL_ATTRIBUTION_PARAMS.some(
		(param) => param.startsWith('utm_') && params.get(param)
	)
	if (!isCampaign || readCapitalAttribution()) return

	const attribution: CapitalAttribution = {
		landingPath: window.location.pathname.slice(0, MAX_VALUE_LENGTH)
	}
	CAPITAL_ATTRIBUTION_PARAMS.forEach((param) => {
		const value = params.get(param)
		if (value) attribution[param] = value.slice(0, MAX_VALUE_LENGTH)
	})
	const { referrer } = document
	if (referrer) attribution.referrer = referrer.slice(0, MAX_VALUE_LENGTH)

	try {
		window.localStorage.setItem(
			ATTRIBUTION_KEY,
			JSON.stringify({ ...attribution, storedAt: Date.now() })
		)
	} catch {
		// private mode — attribution is analytics, never worth breaking a render
	}
}
